import React, { useState, useEffect } from "react";
import { MessageCircle } from "lucide-react";
import { trackWhatsAppClick } from "../lib/analytics";

const PHONE = "393204459052";

/**
 * Floating WhatsApp button.
 *
 * Props:
 *  - courseName?: string  → if present, pre-fills a contextual message asking about that course
 */
export default function WhatsAppFloat({ courseName }) {
  const [showLabel, setShowLabel] = useState(false);

  useEffect(() => {
    // After a short delay, reveal the "Chatta con noi" label to attract attention (mobile-friendly)
    const t = setTimeout(() => setShowLabel(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const message = courseName
    ? `Salve, vorrei maggiori info sul corso "${courseName}"`
    : "Salve, vorrei maggiori info sui corsi";

  const href = `https://api.whatsapp.com/send?phone=${PHONE}&text=${encodeURIComponent(message)}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-6 right-6 z-40 group"
      aria-label="Scrivici su WhatsApp"
      data-testid="whatsapp-float-btn"
      onClick={() => trackWhatsAppClick({ context: courseName || "generic" })}
    >
      <div className="flex items-center gap-2.5">
        {/* Always-visible label pill (mobile-friendly). Fades in after mount. */}
        <div
          className={`hidden sm:flex items-center bg-black/90 backdrop-blur-md border border-white/15 rounded-full pl-4 pr-5 py-2.5 text-white text-sm shadow-2xl transition-all duration-500 ${
            showLabel ? "opacity-100 translate-x-0" : "opacity-0 translate-x-3 pointer-events-none"
          }`}
        >
          <span className="whitespace-nowrap">Chatta con noi</span>
        </div>

        {/* Icon button with pulsing badge */}
        <div className="relative">
          <div className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
            <MessageCircle size={26} className="text-white" />
          </div>

          {/* Pulsing red notification dot */}
          <span
            className="absolute -top-0.5 -right-0.5 flex h-5 w-5"
            aria-hidden="true"
          >
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 border-2 border-[#0a0a0a] items-center justify-center text-[10px] font-bold text-white leading-none">
              1
            </span>
          </span>
        </div>
      </div>
    </a>
  );
}
